import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Chip,
  ClickAwayListener,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import PerfectScrollbar from "react-perfect-scrollbar";

import MainCard from "ui-component/cards/MainCard";
import Transitions from "ui-component/extended/Transitions";
import User1 from "assets/images/users/user-round.svg";

import { IconLogout, IconSettings, IconUser } from "@tabler/icons";
import { redirectUrl, baseUrl } from "config";
import axios from "axios";

const ProfileSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const customization = useSelector((state) => state.customization);
  const userDetail = useSelector((state) => state.common.userDetail);

  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const anchorRef = useRef(null);
  const prevOpen = useRef(open);

  useEffect(() => {
    if (prevOpen.current && !open) anchorRef.current?.focus();
    prevOpen.current = open;
  }, [open]);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(
    (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      setOpen(false);
    },
    []
  );

  const handleListItemClick = useCallback(
    (event, index, route = "") => {
      setSelectedIndex(index);
      handleClose(event);
      if (route) navigate(route);
    },
    [handleClose, navigate]
  );

  const handleLogout = useCallback(() => {
    axios
      .post(`${baseUrl}/logout`, {}, { withCredentials: true })
      .then(() => redirectUrl())
      .catch((err) => {
        console.error("Logout failed", err);
      });
  }, []);

  return (
    <>
      <Chip
        ref={anchorRef}
        onClick={handleToggle}
        icon={
          <Avatar
            src={User1}
            sx={{
              ...theme.typography.mediumAvatar,
              margin: "8px 0 8px 8px !important",
              cursor: "pointer",
            }}
          />
        }
        label={<IconSettings stroke={1.5} size="1.5rem" color={theme.palette.primary.main} />}
        variant="outlined"
        color="primary"
        sx={{
          height: 48,
          borderRadius: "27px",
          borderColor: theme.palette.primary.light,
          backgroundColor: theme.palette.primary.light,
          transition: "all .2s ease-in-out",
          "&:hover, &[aria-controls='menu-list-grow']": {
            borderColor: theme.palette.primary.main,
            backgroundColor: `${theme.palette.primary.main} !important`,
            color: theme.palette.primary.light,
            "& svg": { stroke: theme.palette.primary.light },
          },
          "& .MuiChip-label": { lineHeight: 0 },
        }}
        aria-controls={open ? "menu-list-grow" : undefined}
        aria-haspopup="true"
      />

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: "offset", options: { offset: [0, 14] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                  <Box p={1}>
                    <Stack direction="row" spacing={1} p={1} bgcolor="#2196f3" borderRadius={2}>
                      <Avatar sx={{ bgcolor: "#ffc107", color: "white" }} alt="User Avatar">
                        {userDetail?.user[0] || ""}
                      </Avatar>
                      <Box>
                        <Typography variant="h4">{userDetail?.user}</Typography>
                        <Typography variant="subtitle1" fontSize="10px">Profile</Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <PerfectScrollbar style={{ maxHeight: "calc(100vh - 250px)", overflowX: "hidden" }}>
                    <Box pt={0}>
                      <List
                        component="nav"
                        sx={{
                          width: "100%",
                          maxWidth: 350,
                          minWidth: 300,
                          bgcolor: theme.palette.background.paper,
                          borderRadius: "10px",
                          [theme.breakpoints.down("md")]: { minWidth: "100%" },
                          "& .MuiListItemButton-root": { mt: 0.5 },
                        }}
                      >
                        <ListItemButton
                          selected={selectedIndex === 0}
                          onClick={(e) => handleListItemClick(e, 0, "/user/account-profile/profile1")}
                          sx={{ borderRadius: `${customization.borderRadius}px` }}
                        >
                          <ListItemIcon>
                            <IconSettings stroke={1.5} size="1.3rem" />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">Account Settings</Typography>} />
                        </ListItemButton>

                        <ListItemButton
                          selected={selectedIndex === 1}
                          onClick={(e) => handleListItemClick(e, 1, "/user/social-profile/posts")}
                          sx={{ borderRadius: `${customization.borderRadius}px` }}
                        >
                          <ListItemIcon>
                            <IconUser stroke={1.5} size="1.3rem" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Grid container spacing={1} justifyContent="space-between">
                                <Grid item>
                                  <Typography variant="body2">Social Profile</Typography>
                                </Grid>
                                <Grid item>
                                  <Chip
                                    label="02"
                                    size="small"
                                    sx={{
                                      bgcolor: theme.palette.warning.dark,
                                      color: theme.palette.background.default,
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            }
                          />
                        </ListItemButton>

                        <ListItemButton
                          selected={selectedIndex === 4}
                          onClick={handleLogout}
                          sx={{ borderRadius: `${customization.borderRadius}px` }}
                        >
                          <ListItemIcon>
                            <IconLogout stroke={1.5} size="1.3rem" />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">Logout</Typography>} />
                        </ListItemButton>
                      </List>
                    </Box>
                  </PerfectScrollbar>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

export default ProfileSection;
